import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useContent } from "@/components/useContent";
import { API } from "@/assets";
import { Loader2, Mic2, Briefcase, UploadCloud, CheckCircle2 } from "lucide-react";

const inputCls =
  "w-full bg-obsidian/70 border border-white/12 rounded-xl px-4 py-3 text-sm text-ice placeholder:text-ice/35 focus:outline-none focus:border-venom/70 focus:ring-2 focus:ring-venom/20 transition-all duration-300";

const useTitle = (title) => {
  useEffect(() => {
    document.title = `${title} | DGE: Cold-Blooded Records LLC™`;
    return () => { document.title = "DGE: Cold-Blooded Records LLC™ | Official ASCAP® Member Label — Est. 2026"; };
  }, [title]);
};

const ApplyShell = ({ icon: Icon, eyebrow, title, intro, children, testId }) => (
  <main className="bg-obsidian text-ice min-h-screen" data-testid={testId}>
    <Navbar />
    <div className="pt-[70px]">
      <section className="max-w-2xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
        <Reveal className="text-center">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium mb-4 flex items-center justify-center gap-2">
            <Icon size={14} /> {eyebrow}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.08]">{title}</h1>
          <p className="mt-4 text-sm sm:text-base text-ice/60 leading-relaxed">{intro}</p>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="mt-10 bg-charcoal border border-crystal/25 rounded-2xl p-6 sm:p-8">{children}</div>
        </Reveal>
      </section>
    </div>
    <Footer />
  </main>
);

const SuccessState = ({ title, message }) => (
  <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-10" data-testid="apply-success">
    <CheckCircle2 size={40} className="text-venom mx-auto" />
    <h2 className="mt-4 font-display text-xl font-bold text-white">{title}</h2>
    <p className="mt-2 text-sm text-ice/60 max-w-sm mx-auto leading-relaxed">{message}</p>
  </motion.div>
);

const FileDrop = ({ label, hint, accept, file, setFile, testId }) => (
  <label className="block cursor-pointer border border-dashed border-white/20 rounded-xl px-4 py-5 text-center hover:border-venom/60 transition-colors" data-testid={`${testId}-drop`}>
    <input type="file" accept={accept} className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} data-testid={`${testId}-input`} />
    <UploadCloud size={20} className={`mx-auto mb-2 ${file ? "text-venom" : "text-ice/40"}`} />
    <span className={`text-xs ${file ? "text-venom" : "text-ice/55"}`}>{file ? file.name : label}</span>
    <span className="block mt-1 text-[10px] text-ice/35">{hint}</span>
  </label>
);

export function TalentPage() {
  useTitle("Get Signed");
  const content = useContent();
  const [form, setForm] = useState({ stage_name: "", real_name: "", email: "", phone: "", genre: "", location: "", demo_link: "", links: "", message: "" });
  const [ageOk, setAgeOk] = useState(false);
  const [demo, setDemo] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!ageOk) {
      setError("Please confirm your age status before submitting.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("age_confirmed", ageOk ? "true" : "false");
      if (demo) fd.append("demo", demo);
      const res = await fetch(`${API}/talent`, { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Submission failed");
      setDone(data.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <ApplyShell
      icon={Mic2}
      eyebrow="Talent Submissions & Demo Scouting"
      title="Get signed."
      intro={content.talent_intro}
      testId="talent-page"
    >
      {done ? (
        <SuccessState title="Submission received." message={done} />
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="talent-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <input required value={form.stage_name} onChange={set("stage_name")} placeholder="Artist / stage name *" className={inputCls} data-testid="talent-input-name" />
            <input value={form.real_name} onChange={set("real_name")} placeholder="Real (legal) name" className={inputCls} data-testid="talent-input-realname" />
            <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} data-testid="talent-input-email" />
            <input value={form.phone} onChange={set("phone")} placeholder="Direct phone number" className={inputCls} data-testid="talent-input-phone" />
            <input value={form.genre} onChange={set("genre")} placeholder="Genre (Trap, Jersey Drill, R&B, Soul, Pop…)" className={inputCls} data-testid="talent-input-genre" />
            <input value={form.location} onChange={set("location")} placeholder="City, State / Country" className={inputCls} data-testid="talent-input-location" />
          </div>
          <input type="url" value={form.demo_link} onChange={set("demo_link")} placeholder="Demo stream link (unlisted SoundCloud, Google Drive, Dropbox…)" className={inputCls} data-testid="talent-input-demolink" />
          <input value={form.links} onChange={set("links")} placeholder="EPK or social links (Instagram, TikTok, Spotify…)" className={inputCls} data-testid="talent-input-links" />
          <textarea rows={4} value={form.message} onChange={set("message")} placeholder="Your artist pitch — background, goals, and why you want to join DGE." className={`${inputCls} resize-none`} data-testid="talent-input-message" />
          <FileDrop label="…or attach a demo file (optional)" hint="WAV, MP3, M4A, OGG or FLAC — max 30MB" accept=".wav,.mp3,.m4a,.ogg,.flac,audio/*" file={demo} setFile={setDemo} testId="talent-demo" />
          <label className="flex items-start gap-2.5 text-xs text-ice/60 cursor-pointer leading-relaxed" data-testid="talent-age-label">
            <input type="checkbox" checked={ageOk} onChange={(e) => setAgeOk(e.target.checked)} className="accent-[#39FF14] w-4 h-4 mt-0.5" data-testid="talent-input-age" />
            I confirm I am 18 or older, or submitting with a parent/guardian's consent. My submission contains original content with rights cleared or licensed instrumentals.
          </label>
          {error && <p className="text-sm text-red-400" data-testid="talent-error">{error}</p>}
          <button type="submit" disabled={sending} className="w-full inline-flex items-center justify-center gap-2 bg-venom text-obsidian font-bold text-sm px-6 py-4 rounded-full hover:glow-venom active:scale-[0.98] disabled:opacity-60 transition-all duration-300" data-testid="talent-submit">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Mic2 size={16} />}
            {sending ? "Sending…" : "Submit Demo to A&R"}
          </button>
          <p className="text-[11px] text-ice/35 text-center">Reviewed directly by DGE label executives. You&rsquo;ll get a confirmation email.</p>
        </form>
      )}
    </ApplyShell>
  );
}

const POSITIONS_FALLBACK = [
  "Mix / Master Engineer",
  "Video Editor / Content Creator",
  "Social Media Manager",
  "A&R / Talent Scout",
];

export function CareersPage() {
  useTitle("Careers");
  const content = useContent();
  const [positions, setPositions] = useState(POSITIONS_FALLBACK);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", position: "", links: "", resume_link: "", message: "" });
  const [resume, setResume] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch(`${API}/careers/positions`)
      .then((r) => r.json())
      .then((d) => {
        const open = (d.positions || []).map((p) => p.title);
        if (open.length > 0) {
          setPositions(open);
          setForm((f) => ({ ...f, position: f.position || open[0] }));
        }
      })
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, k === "position" ? (v || positions[0]) : v));
      if (resume) fd.append("resume", resume);
      const res = await fetch(`${API}/careers`, { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Submission failed");
      setDone(data.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <ApplyShell
      icon={Briefcase}
      eyebrow="Careers At The Label"
      title="Join the team."
      intro={content.careers_intro}
      testId="careers-page"
    >
      {done ? (
        <SuccessState title="Application received." message={done} />
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="careers-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <input required value={form.name} onChange={set("name")} placeholder="Full name *" className={inputCls} data-testid="careers-input-name" />
            <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} data-testid="careers-input-email" />
            <input value={form.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} data-testid="careers-input-phone" />
            <input value={form.city} onChange={set("city")} placeholder="City / State" className={inputCls} data-testid="careers-input-city" />
          </div>
          <select value={form.position || positions[0]} onChange={set("position")} className={inputCls} data-testid="careers-input-position">
            {positions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input value={form.links} onChange={set("links")} placeholder="Portfolio / work samples link (website, reel, mix credits…)" className={inputCls} data-testid="careers-input-links" />
          <input type="url" value={form.resume_link} onChange={set("resume_link")} placeholder="Resume / CV link (Google Drive or Dropbox PDF) — or attach below" className={inputCls} data-testid="careers-input-resumelink" />
          <textarea rows={4} value={form.message} onChange={set("message")} placeholder="Cover letter / brief introduction — why DGE?" className={`${inputCls} resize-none`} data-testid="careers-input-message" />
          <FileDrop label="…or attach a resume file (optional)" hint="PDF, DOC, DOCX, TXT or image — max 8MB" accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png" file={resume} setFile={setResume} testId="careers-resume" />
          {error && <p className="text-sm text-red-400" data-testid="careers-error">{error}</p>}
          <button type="submit" disabled={sending} className="w-full inline-flex items-center justify-center gap-2 bg-venom text-obsidian font-bold text-sm px-6 py-4 rounded-full hover:glow-venom active:scale-[0.98] disabled:opacity-60 transition-all duration-300" data-testid="careers-submit">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Briefcase size={16} />}
            {sending ? "Sending…" : "Apply to Join DGE"}
          </button>
          <p className="text-[11px] text-ice/35 text-center">Leadership reviews every application. You&rsquo;ll get a confirmation email.</p>
        </form>
      )}
    </ApplyShell>
  );
}
